import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Vault as VaultIcon, CurrencyCircleDollar, FilePdf } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Vault = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [transactionRef, setTransactionRef] = useState('');

  useEffect(() => {
    fetchLoans();
  }, [statusFilter]);

  const fetchLoans = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const { data } = await axios.get(`${API_URL}/api/loans`, {
        params,
        withCredentials: true
      });
      setLoans(data);
    } catch (error) {
      toast.error('Failed to fetch loans');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/payments`, {
        loan_id: selectedLoan.loan_id,
        amount: parseFloat(paymentAmount),
        payment_mode: paymentMode,
        transaction_ref: transactionRef || null
      }, { withCredentials: true });
      
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setPaymentAmount('');
      setTransactionRef('');
      fetchLoans();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const downloadReceipt = async (loanId) => {
    try {
      const response = await axios.get(`${API_URL}/api/loans/${loanId}/receipt?format=pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${loanId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A227]"></div>
      </div>
    );
  }

  return (
    <div data-testid="vault-page">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Vault
        </h1>
        <p className="text-[#6B7280] mt-2">View and manage all active loans</p>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="mb-4 flex items-center gap-4">
          <Label>Filter by Status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Loans</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loans.length === 0 ? (
          <div className="text-center py-12">
            <VaultIcon size={48} className="mx-auto text-[#6B7280] mb-4" weight="duotone" />
            <p className="text-[#6B7280]">No loans found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left">
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Loan ID</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Customer</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Amount</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Outstanding</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Status</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan) => (
                  <tr 
                    key={loan.loan_id} 
                    className="border-b border-[#E5E7EB] hover:bg-[#F9F9FB] transition-colors"
                    data-testid={`loan-row-${loan.loan_id}`}
                  >
                    <td className="py-3 font-mono text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {loan.loan_id}
                    </td>
                    <td className="py-3 font-medium text-[#1A1A1A]">{loan.customer_name}</td>
                    <td className="py-3 font-mono-num" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatCurrency(loan.loan_amount)}
                    </td>
                    <td className="py-3 font-mono-num" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatCurrency(loan.outstanding_balance)}
                    </td>
                    <td className="py-3">
                      {loan.status === 'active' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-yellow-50 text-yellow-700 text-xs border border-yellow-200">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs border border-green-200">
                          Closed
                        </span>
                      )}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        {loan.status === 'active' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedLoan(loan);
                              setShowPaymentDialog(true);
                            }}
                            className="bg-[#C9A227] hover:bg-[#A9851F]"
                            data-testid={`payment-button-${loan.loan_id}`}
                          >
                            <CurrencyCircleDollar size={16} className="mr-1" />
                            Pay
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadReceipt(loan.loan_id)}
                          data-testid={`receipt-button-${loan.loan_id}`}
                        >
                          <FilePdf size={16} className="mr-1" />
                          Receipt
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {selectedLoan && (
            <form onSubmit={handlePayment} className="space-y-4 mt-4">
              <div>
                <Label>Loan ID</Label>
                <Input value={selectedLoan.loan_id} disabled />
              </div>
              
              <div>
                <Label>Outstanding Balance</Label>
                <Input value={formatCurrency(selectedLoan.outstanding_balance)} disabled />
              </div>
              
              <div>
                <Label htmlFor="payment-amount">Payment Amount *</Label>
                <Input
                  id="payment-amount"
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  required
                  data-testid="payment-amount-input"
                />
              </div>
              
              <div>
                <Label htmlFor="payment-mode">Payment Mode *</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger data-testid="payment-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="transaction-ref">Transaction Reference</Label>
                <Input
                  id="transaction-ref"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  placeholder="UPI ID / Transaction ID"
                  data-testid="transaction-ref-input"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#C9A227] hover:bg-[#A9851F]" data-testid="submit-payment-button">
                  Record Payment
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vault;
